'use client';
import { faArrowDown, faArrowUp } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';

import VideosPagination from '@/modules/players/v2/tabs/videos/VideosPagination';
import VideosTournamentsDropdown from '@/modules/players/v2/tabs/videos/VideosTournamentsDropdown';
import {
  getYouTubeVideoData,
  isValidYoutubeVideo,
} from '@/modules/players/v2/tabs/videos/videoUtils';
import { YoutubeCards } from '@/modules/players/v2/tabs/videos/YoutubeCards';
import { PlayerVideosByTournament, PlayerVideosTournaments } from '@/types/players.types';

interface AllTournamentsVideosWrapperProps {
  tournaments: PlayerVideosTournaments[];
  videos: PlayerVideosByTournament[];
  totalRecords: number;
}

const INITIAL_VIDEO_COUNT = 6;

export const AllTournamentsVideosWrapper = ({
  tournaments,
  videos,
  totalRecords,
}: AllTournamentsVideosWrapperProps) => {
  const [showAllVideos, setShowAllVideos] = useState(false);

  return (
    <>
      <div className='mb-6'>
        <h2 className='mb-1.5 text-sm text-gray-700'>Tournaments</h2>
        <VideosTournamentsDropdown tournaments={tournaments} />
      </div>

      {videos.map((tournament, index) => {
        const validVideos = tournament.highlights.filter((video) =>
          isValidYoutubeVideo(video.link)
        );
        if (validVideos.length === 0) return null;

        const displayedVideos = showAllVideos
          ? validVideos
          : validVideos.slice(0, INITIAL_VIDEO_COUNT);

        return (
          <div key={`${tournament.tournamentTitle}-${index}`}>
            <h1 className='mb-6 text-2xl font-bold text-gray-900'>{tournament.tournamentTitle}</h1>
            <div className='mb-6 grid w-full grid-cols-1 gap-6 sm:grid-cols-3'>
              {displayedVideos.map((video) => {
                const url = video.link;
                const { videoId, start } = getYouTubeVideoData(url);
                return (
                  <YoutubeCards
                    key={`${videoId}-${url}-${video.teamOnePlayerOneFirstName}-${video.teamTwoPlayerOneFirstName}`}
                    youtubeVideoId={videoId}
                    start={start}
                    video={video}
                  />
                );
              })}
            </div>
            {validVideos.length > INITIAL_VIDEO_COUNT && (
              <div className='mb-8 mt-6 flex w-full justify-center'>
                <button
                  onClick={() => setShowAllVideos(!showAllVideos)}
                  className='text-semibold text-brand-700'
                >
                  {showAllVideos ? 'Show Less' : 'Show More'}
                  <FontAwesomeIcon
                    icon={showAllVideos ? faArrowUp : faArrowDown}
                    className='ml-1'
                  />
                </button>
              </div>
            )}
          </div>
        );
      })}

      {!videos.some((tournament) =>
        tournament.highlights.some((video) => isValidYoutubeVideo(video.link))
      ) && (
        <div className='mb-4 flex w-full justify-center rounded-lg border bg-white shadow-md'>
          <div className='py-8 text-gray-900'>No videos found for this player.</div>
        </div>
      )}

      <VideosPagination totalRecords={totalRecords} pageSize={3} />
    </>
  );
};
